import {
    applySetCode,
    applySetControls,
    applySetDocumentConfigs,
    applySetSimulationControlsValue,
    applyUpdateSimulationTimeStep,
    applyUpdateSimulationTimeStepUnit,
} from "../actions/simulationActions";
import {performDocumentMutation} from "../documentMutations";
import type {DocumentStoreState} from "../types";

import type {SliceDependencies} from "./types";

type SimulationSliceState = Pick<
    DocumentStoreState,
    | "updateSimulationTimeStep"
    | "updateSimulationTimeStepUnit"
    | "setSimulationControls"
    | "setSimulationCode"
    | "setDocumentConfigs"
    | "setSimulationControlsValue"
>;

export function createSimulationSlice(deps: SliceDependencies): SimulationSliceState {
    const {primitives, get, currentUserService} = deps;

    return {
        updateSimulationTimeStep: (value) => {
            const document = get().document;
            if (!document?.simulation) {
                return;
            }
            performDocumentMutation(primitives, (draft) => applyUpdateSimulationTimeStep(draft, value), {
                actionType: "updateSimulationTimeStep",
                canUndo: true,
                shouldGenerateActionRecord: true,
                updatesDocumentTimestamp: true,
            });
        },

        updateSimulationTimeStepUnit: (unit) => {
            const document = get().document;
            if (!document?.simulation) {
                return;
            }
            performDocumentMutation(primitives, (draft) => applyUpdateSimulationTimeStepUnit(draft, unit), {
                actionType: "updateSimulationTimeStepUnit",
                canUndo: true,
                shouldGenerateActionRecord: true,
                updatesDocumentTimestamp: true,
            });
        },

        setSimulationControls: (controls) => {
            if (!controls) {
                return;
            }
            performDocumentMutation(primitives, (draft) => applySetControls(draft, controls), {
                actionType: "setControls",
                canUndo: true,
                shouldGenerateActionRecord: true,
                updatesDocumentTimestamp: true,
            });
        },

        setSimulationCode: (code, hasErrors) => {
            if (code === undefined) {
                return;
            }
            performDocumentMutation(primitives, (draft) => applySetCode(draft, code, hasErrors), {
                actionType: "setCode",
                canUndo: false,
                shouldGenerateActionRecord: true,
                updatesDocumentTimestamp: true,
            });
        },

        setDocumentConfigs: (configs) => {
            if (!configs || configs.length === 0) {
                return;
            }
            const currentUser = currentUserService?.getCurrentUser();
            if (!currentUser) {
                return;
            }
            const document = get().document;
            if (!document?.configs) {
                return;
            }
            performDocumentMutation(primitives, (draft) => applySetDocumentConfigs(draft, configs, currentUser.uid), {
                actionType: "setDocumentConfigs",
                canUndo: false,
                shouldGenerateActionRecord: false,
                updatesDocumentTimestamp: false,
            });
        },

        setSimulationControlsValue: (elementUid, controlUid, value) => {
            if (!elementUid || !controlUid || value === undefined) {
                return;
            }
            const currentUser = currentUserService?.getCurrentUser();
            if (!currentUser) {
                return;
            }
            const document = get().document;
            if (!document?.configs) {
                return;
            }
            performDocumentMutation(
                primitives,
                (draft) => applySetSimulationControlsValue(draft, elementUid, controlUid, value, currentUser.uid),
                {
                    actionType: "setSimulationControlsValue",
                    canUndo: true,
                    shouldGenerateActionRecord: false,
                    updatesDocumentTimestamp: false,
                },
            );
        },
    };
}
